import * as React from 'react';
import { PROVIDERS, AGE_GROUPS, AGE_GROUP_RANGES } from '../data';
import { Icon } from '../icons.jsx';
import { Badge, Button } from '../ui.jsx';

const PRIVACY_MINIMUM = 3;

const getWaitlistCount = (provider, ageGroup) => {
  const match = provider.availability[ageGroup].note.match(/Waitlist · (\d+) ahead/i);
  return match ? Number(match[1]) : 0;
};

const totalFor = (providers, selector) =>
  providers.reduce((sum, provider) => sum + selector(provider), 0);

const GROUPS = [
  {
    name: 'Central Philadelphia',
    neighborhoods: ['Fairmount', 'Northern Liberties', 'Graduate Hospital'],
  },
  {
    name: 'South Philadelphia',
    neighborhoods: ['Queen Village', 'Bella Vista', 'Point Breeze'],
  },
];

export const PartnerDashboardScreen = ({ go }) => {
  const totalOpenings = totalFor(PROVIDERS, (provider) => provider.spots);
  const totalWaiting = totalFor(PROVIDERS, (provider) =>
    AGE_GROUPS.reduce(
      (sum, ageGroup) => sum + getWaitlistCount(provider, ageGroup),
      0,
    ),
  );
  const confirmedProviders = PROVIDERS.filter(
    (provider) => provider.availabilityUpdatedAt,
  ).length;
  const participationRate = Math.round((confirmedProviders / PROVIDERS.length) * 100);

  const ageRows = AGE_GROUPS.map((ageGroup) => ({
    ageGroup,
    openings: totalFor(PROVIDERS, (provider) => provider.availability[ageGroup].spots),
    waiting: totalFor(PROVIDERS, (provider) => getWaitlistCount(provider, ageGroup)),
  }));
  const scaleMax = Math.max(...ageRows.flatMap((row) => [row.openings, row.waiting]), 1);

  const neighborhoodGroups = GROUPS.map((group) => {
    const providers = PROVIDERS.filter((provider) =>
      group.neighborhoods.includes(provider.neighborhood),
    );
    return {
      ...group,
      providers: providers.length,
      openings: totalFor(providers, (provider) => provider.spots),
      waiting: totalFor(providers, (provider) =>
        AGE_GROUPS.reduce(
          (sum, ageGroup) => sum + getWaitlistCount(provider, ageGroup),
          0,
        ),
      ),
      acceptingSubsidy: providers.filter((provider) => provider.subsidy).length,
    };
  });

  return (
    <div className="partner-dashboard route-in">
      <section className="partner-hero">
        <div className="container partner-hero-grid">
          <div>
            <Badge tone="brand" icon="shield">Illustrative Philadelphia pilot</Badge>
            <h1>Where child care access needs attention</h1>
            <p>
              A privacy-preserving view of provider-confirmed openings, waitlist pressure,
              and information freshness. No individual family records are shown.
            </p>
          </div>
          <div className="partner-actions">
            <Button variant="secondary" onClick={() => go('compass')}>View COMPASS concept</Button>
            <Button onClick={() => go('home', { audience: 'partners' })}>Partner overview</Button>
          </div>
        </div>
      </section>

      <section className="container partner-content">
        <div className="partner-scope">
          <div>
            <span>Prototype sample</span>
            <strong>Philadelphia provider records</strong>
          </div>
          <div>
            <span>Reporting window</span>
            <strong>Current provider snapshot</strong>
          </div>
          <div>
            <span>Privacy rule</span>
            <strong>At least {PRIVACY_MINIMUM} providers per area</strong>
          </div>
        </div>

        <div className="partner-summary" aria-label="Pilot summary">
          <div>
            <Icon name="home" size={18}/>
            <strong>{PROVIDERS.length}</strong>
            <span>illustrative providers</span>
          </div>
          <div>
            <Icon name="spark" size={18}/>
            <strong>{totalOpenings}</strong>
            <span>current openings</span>
          </div>
          <div>
            <Icon name="user" size={18}/>
            <strong>{totalWaiting}</strong>
            <span>waitlist positions reported</span>
          </div>
          <div>
            <Icon name="check-circle" size={18}/>
            <strong>{participationRate}%</strong>
            <span>recently confirmed</span>
          </div>
        </div>

        <div className="partner-layout">
          <section className="partner-panel" aria-labelledby="age-pressure-title">
            <div className="partner-panel-heading">
              <div>
                <span>Supply and demand</span>
                <h2 id="age-pressure-title">Pressure by age group</h2>
              </div>
              <Badge tone="neutral">Provider supplied</Badge>
            </div>
            <div className="partner-age-list">
              {ageRows.map((row) => (
                <div className="partner-age-row" key={row.ageGroup}>
                  <div>
                    <strong>{row.ageGroup}</strong>
                    <span>{AGE_GROUP_RANGES[row.ageGroup]}</span>
                  </div>
                  <div className="partner-bars">
                    <div>
                      <span style={{ width: `${(row.openings / scaleMax) * 100}%` }}/>
                    </div>
                    <div>
                      <span style={{ width: `${(row.waiting / scaleMax) * 100}%` }}/>
                    </div>
                  </div>
                  <div className="partner-age-values">
                    <span><b>{row.openings}</b> open</span>
                    <span><b>{row.waiting}</b> waiting</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="partner-legend">
              <span><i className="is-open"/>Current openings</span>
              <span><i className="is-wait"/>Reported waitlist positions</span>
            </div>
          </section>

          <aside className="partner-panel partner-freshness" aria-labelledby="freshness-title">
            <div className="partner-panel-heading">
              <div>
                <span>Data quality</span>
                <h2 id="freshness-title">Availability freshness</h2>
              </div>
            </div>
            <div className="partner-freshness-value">
              <strong>{confirmedProviders} of {PROVIDERS.length}</strong>
              <span>providers have a recorded confirmation time</span>
            </div>
            <div className="partner-progress" aria-label={`${participationRate}% confirmed`}>
              <span style={{ width: `${participationRate}%` }}/>
            </div>
            <p>
              Missing confirmation does not mean zero openings. Partners can focus outreach
              on providers whose information needs a refresh.
            </p>
            <Button variant="secondary" full onClick={() => go('textdemo')}>
              See the weekly text check-in
            </Button>
          </aside>
        </div>

        <section className="partner-panel" aria-labelledby="area-title">
          <div className="partner-panel-heading">
            <div>
              <span>Neighborhood view</span>
              <h2 id="area-title">Areas meeting the privacy threshold</h2>
            </div>
            <Badge tone="brand" icon="shield">No family names</Badge>
          </div>
          <div className="partner-table-wrap">
            <table className="partner-table">
              <thead>
                <tr>
                  <th>Area</th>
                  <th>Providers</th>
                  <th>Openings</th>
                  <th>Waitlist pressure</th>
                  <th>Accept subsidy</th>
                  <th>Signal</th>
                </tr>
              </thead>
              <tbody>
                {neighborhoodGroups.map((group) => (
                  <tr key={group.name}>
                    <th scope="row">
                      {group.name}
                      <span>{group.neighborhoods.join(', ')}</span>
                    </th>
                    <td>{group.providers}</td>
                    <td>{group.openings}</td>
                    <td>{group.waiting}</td>
                    <td>{group.acceptingSubsidy} of {group.providers}</td>
                    <td>
                      <Badge tone={group.waiting > group.openings ? 'wait' : 'open'}>
                        {group.waiting > group.openings ? 'Capacity gap' : 'Monitor'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="partner-privacy-note">
            <Icon name="shield" size={18}/>
            <p>
              Exact neighborhood results would be hidden when fewer than {PRIVACY_MINIMUM}
              participating providers contribute data. This prototype combines nearby areas
              to demonstrate that safeguard.
            </p>
          </div>
        </section>
      </section>
    </div>
  );
};
